import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import {
  Button,
  Card,
  Flex,
  Grid,
  Input,
  Modal,
  Pagination,
  Result,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import useTitleStore from "../../store/titleStore";
import useDebounce from "../../hooks/useDebounce";
import { useTableHeight } from "../../hooks/useTableHeight";
import { useMe } from "../../hooks/useMe";
import { complaintService } from "../../services/complaint.service";
import { canReadComplaints } from "../../utils/complaintAccess";
import DetailSection from "../InstitutionalVerification/components/DetailSection";

const CATEGORY_COLORS = {
  complaint: "red",
  system_issue: "orange",
  suggestion: "blue",
  other: "default",
};

const CATEGORY_OPTIONS = [
  { value: "complaint", label: "Pengaduan" },
  { value: "system_issue", label: "Kendala Sistem" },
  { value: "suggestion", label: "Saran dan Masukan" },
  { value: "other", label: "Lainnya" },
];

const formatDateTime = (value) =>
  value ? format(new Date(value), "dd/MM/yyyy HH:mm") : "-";

export default function HelpCenterIndex() {
  const breakpoint = Grid.useBreakpoint();
  const { setTitle } = useTitleStore();
  const tableHeight = useTableHeight(420);

  const { data: me } = useMe();
  const allowed = canReadComplaints(me?.data?.position);

  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    keyword: undefined,
    category: undefined,
  });
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    setTitle("Pusat Bantuan");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      page: 1,
      keyword: debouncedKeyword || undefined,
    }));
  }, [debouncedKeyword]);

  const { data, isLoading, isRefetching } = useQuery({
    queryKey: ["complaints", dataParams],
    queryFn: () => complaintService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
    enabled: allowed,
  });

  const { data: detailData, isLoading: isDetailLoading } = useQuery({
    queryKey: ["complaint-detail", selectedId],
    queryFn: () => complaintService.findById(selectedId),
    enabled: !!selectedId,
  });
  const detail = detailData?.data;

  if (me && !allowed) {
    return (
      <Card>
        <Result
          status="403"
          title="Akses Ditolak"
          subTitle="Pusat Bantuan hanya dapat dilihat oleh Admin dan Verifikator."
        />
      </Card>
    );
  }

  const columns = [
    {
      title: "No",
      align: "center",
      width: 50,
      render: (_, __, index) =>
        ((data?.pagination?.page || 1) - 1) * dataParams.limit + index + 1,
      fixed: breakpoint.md && "left",
    },
    {
      title: "Nama Lengkap",
      dataIndex: "fullName",
      width: 200,
      render: (val, record) => (
        <Flex vertical>
          <Typography.Text strong>{val}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {record.institutionName || "-"}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: "Kontak",
      dataIndex: "email",
      width: 220,
      render: (val, record) => (
        <Flex vertical>
          <Typography.Text>{val}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {record.phone || "-"}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: "Kategori",
      dataIndex: "category",
      width: 160,
      render: (val, record) => (
        <Tag color={CATEGORY_COLORS[val] || "default"}>
          {record.categoryLabel}
        </Tag>
      ),
    },
    {
      title: "Pesan",
      dataIndex: "message",
      render: (val) => (
        <Typography.Text ellipsis={{ tooltip: false }} style={{ maxWidth: 320 }}>
          {val}
        </Typography.Text>
      ),
    },
    {
      title: "Tanggal Masuk",
      dataIndex: "createdAt",
      width: 150,
      render: formatDateTime,
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      width: 70,
      fixed: "right",
      render: (record) => (
        <Button
          aria-label="Lihat detail pesan"
          icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
          color="default"
          variant="filled"
          onClick={() => setSelectedId(record.uuid)}
        />
      ),
    },
  ];

  return (
    <Card styles={{ body: { paddingLeft: 0, paddingRight: 0 } }}>
      <Flex
        justify="space-between"
        gap={20}
        style={{ paddingInline: 24, marginBottom: 24 }}
      >
        <Input
          style={{ width: 320 }}
          prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
          placeholder="Cari"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          allowClear
        />
        <Select
          allowClear
          placeholder="Filter Kategori"
          style={{ width: 160 }}
          options={CATEGORY_OPTIONS}
          value={dataParams.category}
          onChange={(value) =>
            setDataParams((prev) => ({ ...prev, page: 1, category: value }))
          }
        />
      </Flex>

      <Table
        columns={columns}
        pagination={false}
        size="small"
        loading={isLoading || isRefetching}
        rowKey="uuid"
        scroll={{ y: tableHeight, x: 1000 }}
        dataSource={data?.data || []}
        locale={{ emptyText: "Belum ada pesan masuk" }}
      />
      <Pagination
        style={{ marginInline: 24, marginTop: 16 }}
        total={data?.pagination?.total || 0}
        showSizeChanger={{ showSearch: false }}
        pageSizeOptions={["10", "20", "50"]}
        current={data?.pagination?.page || dataParams.page}
        pageSize={dataParams.limit}
        align="end"
        onChange={(page, limit) =>
          setDataParams((prev) => ({ ...prev, page, limit }))
        }
      />

      <Modal
        open={!!selectedId}
        title="Detail Pesan"
        onCancel={() => setSelectedId(null)}
        loading={isDetailLoading}
        width={760}
        footer={[
          <Button key="close" onClick={() => setSelectedId(null)}>
            Tutup
          </Button>,
        ]}
      >
        {detail && (
          <div style={{ marginTop: 12 }}>
            <DetailSection
              title="Informasi Pengirim"
              items={[
                { label: "Nama Lengkap", span: 2, children: detail.fullName },
                {
                  label: "Nama Instansi/Lembaga",
                  span: 2,
                  children: detail.institutionName || "-",
                },
                { label: "Alamat Email", children: detail.email },
                { label: "Nomor Telepon", children: detail.phone || "-" },
              ]}
            />
            <DetailSection
              title="Isi Pesan"
              items={[
                {
                  label: "Kategori Pesan",
                  children: (
                    <Tag color={CATEGORY_COLORS[detail.category] || "default"}>
                      {detail.categoryLabel}
                    </Tag>
                  ),
                },
                {
                  label: "Tanggal Masuk",
                  children: formatDateTime(detail.createdAt),
                },
                {
                  label: "Pesan",
                  span: 2,
                  children: (
                    <Typography.Paragraph
                      style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}
                    >
                      {detail.message}
                    </Typography.Paragraph>
                  ),
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </Card>
  );
}
